import { useState, type JSX } from 'react';
import { useRuntime, useStudio } from '../../context';
import { safely } from '../../safe';
import { urlProblem } from './settingsMath';

/** Plugin URLs may be relative to the page, like the bundled examples under /plugins/. */
function absoluteUrl(value: string): string {
  try {
    return new URL(value.trim(), document.baseURI).href;
  } catch {
    return value;
  }
}

export function PluginsTab(): JSX.Element {
  const studio = useStudio();
  const plugins = useRuntime((r) => r.plugins);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const guestStarActive = plugins.some((plugin) => plugin.id === 'com.ohmyprime.guest-star' && plugin.status === 'active');
  const organicActive = plugins.some((plugin) => plugin.id === 'com.ohmypeer.organic-ads' && plugin.status === 'active');
  const problem = urlProblem(absoluteUrl(url), ['http:', 'https:', 'blob:', 'data:']);

  const run = (key: string, work: () => Promise<void>, prefix: string): void => {
    setBusy(key);
    void safely(studio, work, prefix).finally(() => setBusy(null));
  };

  const load = (): void => {
    const target = url.trim();
    if (!target || problem) return;
    run(
      'load',
      async () => {
        await studio.loadPlugin(target);
        setUrl('');
      },
      'Could not load the plugin',
    );
  };

  return (
    <div>
      <p className="ui-warn-text">
        Plugins run inside this page with full access to your scenes, devices and stream. Load a plugin only if you trust its author and
        the server it is loaded from.
      </p>
      <div className="ui-section-title">Built-in plugins</div>
      <div className="ui-row">
        <div className="ui-grow">
          <strong>Perck Organic ads</strong>
          <p className="ui-dim">Show ads you approve in your broadcast. Private presenter scripts stay in your Perck prompter.</p>
        </div>
        <button type="button" className="ui-btn" disabled={busy !== null || organicActive}
          onClick={() => run('organic', () => studio.loadPlugin('/plugins/organic-ads/1.0.0/index.js'), 'Could not enable Organic ads')}>
          {organicActive ? 'Enabled' : 'Enable'}
        </button>
      </div>
      <div className="ui-row">
        <div className="ui-grow">
          <strong>Guest Star</strong>
          <p className="ui-dim">Invite guests through your portal, hear the call and add live guests to your broadcast.</p>
        </div>
        <button type="button" className="ui-btn" disabled={busy !== null || guestStarActive}
          onClick={() => run('guest-star', () => studio.loadPlugin(import.meta.env.DEV ? '/src/plugins/guest-star/index.ts' : '/plugins/guest-star.js'), 'Could not enable Guest Star')}>
          {guestStarActive ? 'Enabled' : 'Enable'}
        </button>
      </div>
      <form
        className="ui-row"
        onSubmit={(e) => {
          e.preventDefault();
          load();
        }}
      >
        <input
          className={problem ? 'ui-input ui-grow invalid' : 'ui-input ui-grow'}
          type="text"
          inputMode="url"
          aria-label="Plugin module URL"
          placeholder="https://example.com/plugin.js or /plugins/example-source.js"
          value={url}
          spellCheck={false}
          onChange={(e) => setUrl(e.target.value)}
        />
        <button type="submit" className="ui-btn primary" disabled={!url.trim() || problem !== null || busy !== null} aria-busy={busy === 'load'}>
          {busy === 'load' ? 'Loading…' : 'Load plugin'}
        </button>
      </form>
      {problem ? <div className="ui-warn-text">{problem}</div> : null}

      <div className="ui-section-title">Plugins in this collection</div>
      {plugins.length === 0 ? <div className="ui-empty">No plugins are loaded.</div> : null}
      {plugins.some((plugin) => plugin.status === 'disabled') ? (
        <p className="ui-dim">Disabled plugins were listed by the collection but have not been run. Load only the ones you trust.</p>
      ) : null}
      {plugins.length > 0 ? (
        <table className="ui-table">
          <thead>
            <tr>
              <th scope="col">Plugin</th>
              <th scope="col">Version</th>
              <th scope="col">Status</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {plugins.map((plugin) => (
              <tr key={plugin.id}>
                <th scope="row" style={{ whiteSpace: 'normal' }}>
                  <div>{plugin.name}</div>
                  <div className="ui-dim ui-selectable" style={{ fontWeight: 400, wordBreak: 'break-all' }}>
                    {plugin.url}
                  </div>
                  {plugin.error ? (
                    <div className="ui-error-text ui-selectable" style={{ fontWeight: 400 }}>
                      {plugin.error}
                    </div>
                  ) : null}
                </th>
                <td>{plugin.version}</td>
                <td className={plugin.status === 'error' ? 'ui-error-text' : plugin.status === 'active' ? '' : 'ui-dim'}>{plugin.status}</td>
                <td>
                  <div className="ui-row" style={{ justifyContent: 'flex-end' }}>
                    {plugin.status === 'active' ? (
                      <button type="button" className="ui-btn" disabled={busy !== null} onClick={() => run(plugin.id, () => studio.unloadPlugin(plugin.id), 'Could not unload the plugin')}>
                        Unload
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="ui-btn primary"
                          disabled={busy !== null}
                          aria-label={`Load ${plugin.name}`}
                          onClick={() => run(plugin.id, () => studio.loadPlugin(plugin.url), 'Could not load the plugin')}
                        >
                          {plugin.status === 'error' ? 'Retry' : 'Load'}
                        </button>
                        <button
                          type="button"
                          className="ui-btn"
                          disabled={busy !== null}
                          aria-label={`Remove ${plugin.name}`}
                          onClick={() => run(plugin.id, () => studio.unloadPlugin(plugin.id), 'Could not remove the plugin')}
                        >
                          Remove
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}
